import React, { useState, useEffect, useCallback , useMemo } from 'react';
import axios from 'axios';
import { QCM_TYPES, qcmTypeLabel, qcmTypeColor } from '../lib/qcmTypes';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Plus, Search, Trash2, ArrowLeft, Copy, Eye, GripVertical, Check,
  X, Settings2, FileText, Users, Clock, Award, ChevronUp, ChevronDown,
  BarChart3, Pencil, CircleDot, CheckSquare, ToggleLeft, Archive, Send, BookOpen,
  Upload, Image as ImageIcon, Loader2, CalendarDays
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { getAuthToken } from '../lib/authToken';
import { QcmPdfButtons } from '../components/qcm/QcmPdfButtons';
import { QcmModulesBadges, QcmModulesPicker } from '../components/qcm/QcmModules';
import { saveBlob } from '../lib/download';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUESTION_TYPES = [
  { value: 'choix_unique', label: 'Choix unique', icon: CircleDot },
  { value: 'choix_multiple', label: 'Choix multiple', icon: CheckSquare },
  { value: 'vrai_faux', label: 'Vrai / Faux', icon: ToggleLeft },
];

const EVAL_TYPES = QCM_TYPES;

const uuid = () => crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36);

// ============================================================
// Question Editor Component
// ============================================================
const QuestionEditor = ({ question, index, onChange, onDelete, onMoveUp, onMoveDown, isFirst, isLast }) => {
  const typeConfig = QUESTION_TYPES.find(t => t.value === question.type) || QUESTION_TYPES[0];
  const TypeIcon = typeConfig.icon;
  const textareaRef = React.useRef(null);
  const imageInputRef = React.useRef(null);
  const [uploadingImg, setUploadingImg] = React.useState(false);
  React.useEffect(() => {
    const node = textareaRef.current;
    if (node) { node.style.height = 'auto'; node.style.height = node.scrollHeight + 'px'; }
  }, [question.texte]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image trop volumineuse (max 5 Mo)');
      e.target.value = '';
      return;
    }
    setUploadingImg(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API_URL}/qcm-images`, fd);
      onChange({ ...question, image_id: res.data.image_id });
      toast.success('Image ajoutée à la question');
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'upload de l'image");
    } finally {
      setUploadingImg(false);
      e.target.value = '';
    }
  };

  const updateField = (field, value) => {
    onChange({ ...question, [field]: value });
  };

  const updateOption = (optionIndex, field, value) => {
    const newOptions = [...question.options];
    newOptions[optionIndex] = { ...newOptions[optionIndex], [field]: value };
    // For choix_unique, ensure only one is correct
    if (field === 'est_correcte' && value && question.type === 'choix_unique') {
      newOptions.forEach((o, i) => {
        if (i !== optionIndex) o.est_correcte = false;
      });
    }
    onChange({ ...question, options: newOptions });
  };

  const addOption = () => {
    onChange({
      ...question,
      options: [...question.options, { id: uuid(), texte: '', est_correcte: false }],
    });
  };

  const removeOption = (optionIndex) => {
    onChange({
      ...question,
      options: question.options.filter((_, i) => i !== optionIndex),
    });
  };

  const setQuestionType = (newType) => {
    let newOptions = question.options;
    if (newType === 'vrai_faux') {
      newOptions = [
        { id: uuid(), texte: 'Vrai', est_correcte: true },
        { id: uuid(), texte: 'Faux', est_correcte: false },
      ];
    } else if (question.type === 'vrai_faux') {
      newOptions = [
        { id: uuid(), texte: '', est_correcte: false },
        { id: uuid(), texte: '', est_correcte: false },
      ];
    }
    // If switching from multiple to unique, keep only first correct
    if (newType === 'choix_unique' && question.type === 'choix_multiple') {
      let foundFirst = false;
      newOptions = newOptions.map(o => {
        if (o.est_correcte && !foundFirst) { foundFirst = true; return o; }
        return { ...o, est_correcte: false };
      });
    }
    onChange({ ...question, type: newType, options: newOptions });
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow" data-testid={`question-editor-${index}`}>
      <div className="flex items-center gap-2 px-4 pt-3 pb-2 border-b border-slate-100">
        <GripVertical className="w-4 h-4 text-slate-300 cursor-grab" />
        <span className="text-sm font-semibold text-slate-500">Q{index + 1}</span>
        <div className="flex-1" />
        <div className="flex items-center gap-1">
          <button onClick={onMoveUp} disabled={isFirst} className="p-1 rounded hover:bg-slate-100 disabled:opacity-30" title="Monter">
            <ChevronUp className="w-4 h-4" />
          </button>
          <button onClick={onMoveDown} disabled={isLast} className="p-1 rounded hover:bg-slate-100 disabled:opacity-30" title="Descendre">
            <ChevronDown className="w-4 h-4" />
          </button>
          <button onClick={() => onChange({ ...question })} className="p-1 rounded hover:bg-slate-100" title="Dupliquer">
            <Copy className="w-4 h-4 text-slate-400" />
          </button>
          <button onClick={onDelete} className="p-1 rounded hover:bg-red-50 text-red-400 hover:text-red-600" title="Supprimer">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Question text + type */}
        <div className="flex gap-3">
          <div className="flex-1">
            <Textarea
              ref={textareaRef}
              placeholder="Saisissez votre question..."
              value={question.texte}
              onChange={(e) => updateField('texte', e.target.value)}
              className="text-base font-medium border-0 border-b-2 border-slate-200 rounded-none px-0 focus-visible:ring-0 focus-visible:border-indigo-500 resize-none overflow-hidden min-h-[36px]"
              rows={1}
              onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
              data-testid={`question-text-${index}`}
            />
          </div>
          <input
            ref={imageInputRef}
            type="file"
            accept=".png,.jpg,.jpeg,.gif,.webp"
            className="hidden"
            onChange={handleImageUpload}
            data-testid={`question-image-input-${index}`}
          />
          <button
            type="button"
            title="Joindre une image à la question"
            onClick={() => imageInputRef.current?.click()}
            disabled={uploadingImg}
            className="self-start p-2 rounded-md text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
            data-testid={`question-image-btn-${index}`}
          >
            {uploadingImg ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
          </button>
          <Select value={question.type} onValueChange={setQuestionType}>
            <SelectTrigger className="w-[180px]" data-testid={`question-type-${index}`}>
              <div className="flex items-center gap-2">
                <TypeIcon className="w-4 h-4" />
                <SelectValue />
              </div>
            </SelectTrigger>
            <SelectContent>
              {QUESTION_TYPES.map(t => (
                <SelectItem key={t.value} value={t.value}>
                  <div className="flex items-center gap-2">
                    <t.icon className="w-4 h-4" />
                    {t.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Image de la question */}
        {question.image_id && (
          <div className="relative inline-block" data-testid={`question-image-preview-${index}`}>
            <img
              src={`${API_URL}/qcm-images/${question.image_id}`}
              alt="Illustration de la question"
              className="max-h-48 rounded-lg border border-slate-200"
            />
            <button
              type="button"
              title="Retirer l'image"
              onClick={() => updateField('image_id', null)}
              className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center shadow hover:bg-red-600"
              data-testid={`question-image-remove-${index}`}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Options */}
        <div className="space-y-2 pl-2">
          {question.options.map((option, oi) => (
            <div key={option.id} className="flex items-center gap-3 group" data-testid={`option-${index}-${oi}`}>
              {question.type === 'choix_unique' ? (
                <button
                  type="button"
                  onClick={() => updateOption(oi, 'est_correcte', true)}
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                    option.est_correcte
                      ? 'border-emerald-500 bg-emerald-500'
                      : 'border-slate-300 hover:border-emerald-400'
                  }`}
                >
                  {option.est_correcte && <div className="w-2 h-2 rounded-full bg-white" />}
                </button>
              ) : question.type === 'choix_multiple' ? (
                <button
                  type="button"
                  onClick={() => updateOption(oi, 'est_correcte', !option.est_correcte)}
                  className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 border-2 transition-colors ${
                    option.est_correcte
                      ? 'border-emerald-500 bg-emerald-500'
                      : 'border-slate-300 hover:border-emerald-400'
                  }`}
                >
                  {option.est_correcte && <Check className="w-3 h-3 text-white" />}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    // For vrai_faux toggle: only one can be correct
                    const newOpts = question.options.map((o, i) => ({ ...o, est_correcte: i === oi }));
                    onChange({ ...question, options: newOpts });
                  }}
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                    option.est_correcte ? 'border-emerald-500 bg-emerald-500' : 'border-slate-300 hover:border-emerald-400'
                  }`}
                >
                  {option.est_correcte && <div className="w-2 h-2 rounded-full bg-white" />}
                </button>
              )}

              {question.type === 'vrai_faux' ? (
                <span className="text-sm font-medium text-slate-700">{option.texte}</span>
              ) : (
                <Input
                  placeholder={`Option ${oi + 1}`}
                  value={option.texte}
                  onChange={(e) => updateOption(oi, 'texte', e.target.value)}
                  className="flex-1 border-0 border-b border-slate-100 rounded-none px-0 focus-visible:ring-0 focus-visible:border-indigo-400 text-sm"
                  data-testid={`option-text-${index}-${oi}`}
                />
              )}

              {question.type !== 'vrai_faux' && question.options.length > 2 && (
                <button
                  onClick={() => removeOption(oi)}
                  className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-50 text-slate-300 hover:text-red-500 transition-all"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}

          {question.type !== 'vrai_faux' && (
            <button
              onClick={addOption}
              className="flex items-center gap-2 text-sm text-indigo-500 hover:text-indigo-700 mt-2 pl-8"
              data-testid={`add-option-${index}`}
            >
              <Plus className="w-4 h-4" /> Ajouter une option
            </button>
          )}
        </div>

        {/* Footer: points + explanation */}
        <div className="flex items-center gap-4 pt-2 border-t border-slate-100">
          <div className="flex items-center gap-2">
            <Label className="text-xs text-slate-500">Points :</Label>
            <Input
              type="number" min={1} max={100}
              value={question.points}
              onChange={(e) => updateField('points', parseInt(e.target.value) || 1)}
              className="w-16 h-7 text-xs"
              data-testid={`question-points-${index}`}
            />
          </div>
          <div className="flex-1">
            <Input
              placeholder="Explication (affichée après correction)..."
              value={question.explication || ''}
              onChange={(e) => updateField('explication', e.target.value)}
              className="h-7 text-xs border-slate-100"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// QCM Builder (Full Editor)
// ============================================================
const QCMBuilder = ({ qcm, onSave, onBack, modules, sequences = [] }) => {
  const [form, setForm] = useState({
    titre: '',
    description: '',
    module_id: '',
    sequence_code: '',
    sequence_name: '',
    competences_visees: [],
    type_evaluation: 'exercice',
    statut: 'brouillon',
    settings: {
      temps_limite_minutes: null,
      seuil_reussite: null,
      navigation_libre: true,
      afficher_resultats: true,
      afficher_corrections: true,
      melanger_questions: false,
      melanger_options: false,
      tentatives_max: null,
    },
    questions: [],
  });
  const [showSettings, setShowSettings] = useState(false);
  const [moduleIds, setModuleIds] = useState(() => (qcm?.modules_rattaches || []).map(m => m.id));
  const [showCsvImport, setShowCsvImport] = useState(false);
  const [csvImporting, setCsvImporting] = useState(false);
  const [saving, setSaving] = useState(false);
  const csvInputRef = React.useRef(null);
  const token = getAuthToken();
  const csvHeaders = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (qcm) {
      setForm({
        titre: qcm.titre || '',
        description: qcm.description || '',
        module_id: qcm.module_id || '',
        sequence_code: qcm.sequence_code || '',
        sequence_name: qcm.sequence_name || '',
        competences_visees: qcm.competences_visees || [],
        type_evaluation: qcm.type_evaluation || 'exercice',
        statut: qcm.statut || 'brouillon',
        settings: { ...form.settings, ...(qcm.settings || {}) },
        questions: qcm.questions || [],
      });
    }
    // eslint-disable-next-line
  }, [qcm]);

  const updateSetting = (key, value) => {
    setForm(prev => ({ ...prev, settings: { ...prev.settings, [key]: value } }));
  };

  const addQuestion = (type = 'choix_unique') => {
    const defaultOptions = type === 'vrai_faux'
      ? [{ id: uuid(), texte: 'Vrai', est_correcte: true }, { id: uuid(), texte: 'Faux', est_correcte: false }]
      : [{ id: uuid(), texte: '', est_correcte: false }, { id: uuid(), texte: '', est_correcte: false }];

    setForm(prev => ({
      ...prev,
      questions: [...prev.questions, {
        id: uuid(), texte: '', type, options: defaultOptions, points: 1, explication: '', obligatoire: true, ordre: prev.questions.length,
      }],
    }));
  };

  const updateQuestion = (index, updated) => {
    setForm(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => i === index ? updated : q),
    }));
  };

  const deleteQuestion = (index) => {
    setForm(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index),
    }));
  };

  const moveQuestion = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= form.questions.length) return;
    const qs = [...form.questions];
    [qs[index], qs[newIndex]] = [qs[newIndex], qs[index]];
    setForm(prev => ({ ...prev, questions: qs }));
  };

  const duplicateQuestion = (index) => {
    const q = form.questions[index];
    const dup = {
      ...JSON.parse(JSON.stringify(q)),
      id: uuid(),
      options: q.options.map(o => ({ ...o, id: uuid() })),
    };
    const qs = [...form.questions];
    qs.splice(index + 1, 0, dup);
    setForm(prev => ({ ...prev, questions: qs }));
  };

  const downloadCsvTemplate = () => {
    const header = 'question;type;option_a;option_b;option_c;option_d;option_e;option_f;bonne_reponse;points;explication';
    const ex1 = 'Quel équipement de protection est obligatoire en entrepôt ?;choix_unique;Chaussures de sécurité;Lunettes de soleil;Chapeau;Gants de jardinage;;;A;1;Les chaussures de sécurité sont obligatoires selon la norme EN ISO 20345';
    const ex2 = 'Quelles sont les catégories CACES R489 ?;choix_multiple;Catégorie 1;Catégorie 3;Catégorie 5;Catégorie 9;;;A,B,C;2;Les catégories 1 3 et 5 font partie du CACES R489';
    const ex3 = 'Le port du gilet haute visibilité est obligatoire en zone de circulation.;vrai_faux;Vrai;Faux;;;;;A;1;';
    const csv = [header, ex1, ex2, ex3].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    saveBlob(blob, 'modele_qcm.csv');
  };

  const handleCsvImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/qcms/parse-csv`, formData, {
        headers: { ...csvHeaders, 'Content-Type': 'multipart/form-data' },
      });
      const imported = res.data.questions || [];
      if (imported.length === 0) {
        toast.error('Aucune question trouvée dans le fichier');
      } else {
        // Re-index ordres to append after existing questions
        const offset = form.questions.length;
        const reindexed = imported.map((q, i) => ({ ...q, ordre: offset + i }));
        setForm(prev => ({ ...prev, questions: [...prev.questions, ...reindexed] }));
        const fmt = res.data.format_detecte === 'google_forms' ? '(format Google Forms)' : '(format interne)';
        toast.success(`${imported.length} question${imported.length > 1 ? 's' : ''} importée${imported.length > 1 ? 's' : ''} ${fmt}`);
        if (res.data.errors?.length > 0) {
          toast.warning(`${res.data.errors.length} ligne(s) ignorée(s)`);
        }
      }
      setShowCsvImport(false);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Erreur de parsing CSV';
      toast.error(detail);
    }
    setCsvImporting(false);
    if (csvInputRef.current) csvInputRef.current.value = '';
  };

  const handleSave = async (newStatut) => {
    if (!form.titre.trim()) { toast.error('Le titre est obligatoire'); return; }
    if (form.questions.length === 0) { toast.error('Ajoutez au moins une question'); return; }
    // Validate questions
    for (let i = 0; i < form.questions.length; i++) {
      const q = form.questions[i];
      if (!q.texte.trim()) { toast.error(`Question ${i + 1} : le texte est vide`); return; }
      if (q.options.some(o => !o.texte.trim())) { toast.error(`Question ${i + 1} : une option est vide`); return; }
      if (!q.options.some(o => o.est_correcte)) { toast.error(`Question ${i + 1} : aucune bonne réponse définie`); return; }
    }
    setSaving(true);
    try {
      await onSave({ ...form, statut: newStatut || form.statut }, moduleIds);
      toast.success(newStatut === 'publie' ? 'QCM publié !' : 'QCM sauvegardé');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
    setSaving(false);
  };

  const totalPoints = form.questions.reduce((s, q) => s + (q.points || 1), 0);

  return (
    <div className="max-w-4xl mx-auto" data-testid="qcm-builder">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="qcm-builder-back">
          <ArrowLeft className="w-4 h-4 mr-1" /> Retour
        </Button>
        <div className="flex-1" />
        <Button variant="outline" size="sm" onClick={() => setShowSettings(true)} data-testid="qcm-settings-btn">
          <Settings2 className="w-4 h-4 mr-1" /> Paramètres
        </Button>
        <Button variant="outline" size="sm" onClick={() => setShowCsvImport(true)} data-testid="qcm-import-csv-btn">
          <Upload className="w-4 h-4 mr-1" /> Importer CSV
        </Button>
        <Button variant="outline" size="sm" onClick={() => handleSave('brouillon')} disabled={saving}>
          Sauvegarder brouillon
        </Button>
        <Button size="sm" onClick={() => handleSave('publie')} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="qcm-publish-btn">
          <Send className="w-4 h-4 mr-1" /> Publier
        </Button>
      </div>

      {/* Title Card */}
      <div className="bg-gradient-to-r from-indigo-600 to-indigo-500 rounded-xl p-6 mb-6 text-white shadow-lg">
        <Input
          placeholder="Titre du QCM"
          value={form.titre}
          onChange={(e) => setForm(prev => ({ ...prev, titre: e.target.value }))}
          className="text-2xl font-bold bg-transparent border-0 border-b-2 border-white/30 rounded-none text-white placeholder:text-white/50 focus-visible:ring-0 focus-visible:border-white px-0 mb-3"
          data-testid="qcm-titre-input"
        />
        <Textarea
          placeholder="Description (optionnelle)..."
          value={form.description}
          onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
          className="bg-transparent border-0 border-b border-white/20 rounded-none text-white/90 placeholder:text-white/40 focus-visible:ring-0 focus-visible:border-white/50 px-0 resize-none text-sm"
          rows={2}
        />
        <div className="flex items-center gap-4 mt-4 text-sm text-white/70">
          <div className="flex items-center gap-1">
            <FileText className="w-4 h-4" /> {form.questions.length} question{form.questions.length > 1 ? 's' : ''}
          </div>
          <div className="flex items-center gap-1">
            <Award className="w-4 h-4" /> {totalPoints} point{totalPoints > 1 ? 's' : ''}
          </div>
          {form.settings.temps_limite_minutes && (
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" /> {form.settings.temps_limite_minutes} min
            </div>
          )}
          {form.settings.seuil_reussite && (
            <div className="flex items-center gap-1">
              Seuil : {form.settings.seuil_reussite}%
            </div>
          )}
        </div>
        {/* Sequence link */}
        <div className="flex gap-3 mt-4">
          <Select
            value={form.sequence_code || 'none'}
            onValueChange={(v) => {
              if (v === 'none') { setForm(prev => ({ ...prev, sequence_code: '', sequence_name: '' })); return; }
              const s = sequences.find(x => x.code === v);
              setForm(prev => ({ ...prev, sequence_code: v, sequence_name: s?.name || '' }));
            }}
          >
            <SelectTrigger className="w-[280px] bg-white/10 border-white/20 text-white text-sm" data-testid="qcm-sequence-select">
              <SelectValue placeholder="Rattacher à une séquence..." />
            </SelectTrigger>
            <SelectContent className="max-h-64">
              <SelectItem value="none">Aucune séquence</SelectItem>
              {sequences.map(s => (
                <SelectItem key={s.code} value={s.code}>{s.code} — {s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={form.type_evaluation || 'exercice'} onValueChange={(v) => setForm(prev => ({ ...prev, type_evaluation: v }))}>
            <SelectTrigger className="w-[180px] bg-white/10 border-white/20 text-white text-sm" data-testid="qcm-type-select">
              <SelectValue placeholder="Type de QCM" />
            </SelectTrigger>
            <SelectContent>
              {EVAL_TYPES.map(t => (
                <SelectItem key={t.value} value={t.value} data-testid={`qcm-type-option-${t.value}`}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {/* Modules rattachés (miroir de « Tests rattachés » côté module) */}
        <div className="mt-3">
          <QcmModulesPicker modules={modules} selectedIds={moduleIds} onChange={setModuleIds} />
          <p className="text-[11px] text-white/60 mt-1">Le rattachement est enregistré avec le QCM ; le drapeau « Test clé » se règle dans la fiche du module.</p>
        </div>
      </div>

      {/* Compétences visées */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 mb-6" data-testid="qcm-competences-section">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-800">Savoir-faire et connaissances visés</h3>
            <p className="text-xs text-slate-400 mt-0.5">Le formateur notera chaque savoir-faire / connaissance : NA / ECA / A. Si toutes sont « Acquis » et que ce QCM est un Test Clé d'un module, le module sera validé automatiquement.</p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setForm(prev => ({ ...prev, competences_visees: [...(prev.competences_visees || []), { id: uuid(), titre: '' }] }))}
            data-testid="qcm-add-competence"
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter
          </Button>
        </div>
        {(form.competences_visees || []).length === 0 ? (
          <p className="text-sm text-slate-400 italic">Aucun savoir-faire / connaissance défini</p>
        ) : (
          <div className="space-y-2">
            {form.competences_visees.map((c, i) => (
              <div key={c.id} className="flex items-center gap-2">
                <span className="text-xs text-slate-400 font-mono w-6">{i + 1}.</span>
                <Input
                  value={c.titre}
                  onChange={(e) => setForm(prev => ({ ...prev, competences_visees: prev.competences_visees.map(x => x.id === c.id ? { ...x, titre: e.target.value } : x) }))}
                  placeholder="Savoir-faire ou connaissance..."
                  className="flex-1"
                  data-testid={`qcm-competence-input-${i}`}
                />
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 text-red-400 hover:text-red-600"
                  onClick={() => setForm(prev => ({ ...prev, competences_visees: prev.competences_visees.filter(x => x.id !== c.id) }))}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Questions */}
      <div className="space-y-4 mb-6">
        {form.questions.map((q, i) => (
          <QuestionEditor
            key={q.id}
            question={q}
            index={i}
            onChange={(updated) => updateQuestion(i, updated)}
            onDelete={() => deleteQuestion(i)}
            onMoveUp={() => moveQuestion(i, -1)}
            onMoveDown={() => moveQuestion(i, 1)}
            isFirst={i === 0}
            isLast={i === form.questions.length - 1}
          />
        ))}
      </div>

      {/* Add Question */}
      <div className="flex items-center justify-center gap-3 py-6">
        {QUESTION_TYPES.map(t => (
          <Button
            key={t.value}
            variant="outline"
            size="sm"
            onClick={() => addQuestion(t.value)}
            className="gap-2"
            data-testid={`add-question-${t.value}`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </Button>
        ))}
      </div>

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-lg" data-testid="qcm-settings-dialog">
          <DialogHeader>
            <DialogTitle>Paramètres du QCM</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-4">
            <div className="space-y-2">
              <Label>Temps limite (minutes)</Label>
              <Input
                type="number" min={0} placeholder="Aucune limite"
                value={form.settings.temps_limite_minutes || ''}
                onChange={(e) => updateSetting('temps_limite_minutes', e.target.value ? parseInt(e.target.value) : null)}
                data-testid="setting-temps-limite"
              />
              <p className="text-xs text-slate-500">Laissez vide pour aucune limite de temps</p>
            </div>
            <div className="space-y-2">
              <Label>Seuil de réussite (%)</Label>
              <Input
                type="number" min={0} max={100} placeholder="Aucun seuil"
                value={form.settings.seuil_reussite || ''}
                onChange={(e) => updateSetting('seuil_reussite', e.target.value ? parseInt(e.target.value) : null)}
                data-testid="setting-seuil"
              />
              <p className="text-xs text-slate-500">Laissez vide si pas de notion de réussite/échec</p>
            </div>
            <div className="space-y-2">
              <Label>Tentatives maximum</Label>
              <Input
                type="number" min={1} placeholder="Illimité"
                value={form.settings.tentatives_max || ''}
                onChange={(e) => updateSetting('tentatives_max', e.target.value ? parseInt(e.target.value) : null)}
              />
            </div>
            <div className="space-y-3 pt-2">
              <div className="flex items-center justify-between">
                <Label>Navigation libre (retour en arrière)</Label>
                <Switch checked={form.settings.navigation_libre} onCheckedChange={(v) => updateSetting('navigation_libre', v)} />
              </div>
              <div className="flex items-center justify-between">
                <Label>Afficher le score à la fin</Label>
                <Switch checked={form.settings.afficher_resultats} onCheckedChange={(v) => updateSetting('afficher_resultats', v)} />
              </div>
              <div className="flex items-center justify-between">
                <Label>Afficher les corrections</Label>
                <Switch checked={form.settings.afficher_corrections} onCheckedChange={(v) => updateSetting('afficher_corrections', v)} />
              </div>
              <div className="flex items-center justify-between">
                <Label>Mélanger les questions</Label>
                <Switch checked={form.settings.melanger_questions} onCheckedChange={(v) => updateSetting('melanger_questions', v)} />
              </div>
              <div className="flex items-center justify-between">
                <Label>Mélanger les options</Label>
                <Switch checked={form.settings.melanger_options} onCheckedChange={(v) => updateSetting('melanger_options', v)} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowSettings(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV Import Dialog */}
      <Dialog open={showCsvImport} onOpenChange={setShowCsvImport}>
        <DialogContent className="max-w-lg" data-testid="csv-import-dialog">
          <DialogHeader>
            <DialogTitle>Importer des questions depuis un CSV</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-slate-600">
              Importez vos questions depuis un fichier CSV. <b>Deux formats supportés</b> : l'export direct de Google Forms et notre format interne.
            </p>

            <Tabs defaultValue="google" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-3">
                <TabsTrigger value="google">Format Google Forms</TabsTrigger>
                <TabsTrigger value="interne">Format interne</TabsTrigger>
              </TabsList>

              <TabsContent value="google">
                <div className="bg-emerald-50 rounded-lg border border-emerald-200 p-4">
                  <p className="text-xs font-semibold text-emerald-800 mb-2">Export Google Forms (détection auto) :</p>
                  <div className="text-[11px] text-emerald-700 font-mono leading-relaxed">
                    <p className="font-bold">Question,Type,Choix,Bonne reponse</p>
                    <p>"Ma question ?","MULTIPLE_CHOICE","Option A","false"</p>
                    <p>"Ma question ?","MULTIPLE_CHOICE","Option B","true"</p>
                    <p>"Ma question ?","MULTIPLE_CHOICE","Option C","false"</p>
                  </div>
                  <p className="text-[10px] text-emerald-600 mt-2">1 ligne par option. Si plusieurs bonnes réponses → auto-détecté comme choix multiple.</p>
                </div>
              </TabsContent>

              <TabsContent value="interne">
                <div className="bg-slate-50 rounded-lg border p-4">
                  <p className="text-xs font-semibold text-slate-700 mb-2">Format interne (séparateur : point-virgule) :</p>
                  <div className="text-[11px] text-slate-500 font-mono leading-relaxed">
                    <p className="font-bold text-slate-700">question;type;option_a;option_b;option_c;option_d;option_e;option_f;bonne_reponse;points;explication</p>
                    <p className="text-slate-400 mt-1">Exemple :</p>
                    <p>Quel EPI est obligatoire ?;<b>choix_unique</b>;Chaussures;Lunettes;Chapeau;;;;<b>A</b>;1;Norme EN ISO</p>
                  </div>
                  <div className="mt-2 text-[10px] text-slate-500 space-y-0.5">
                    <p><b>type</b> : choix_unique, choix_multiple, ou vrai_faux</p>
                    <p><b>bonne_reponse</b> : A, B, C... (séparez par virgule si plusieurs : A,C)</p>
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={downloadCsvTemplate}
                data-testid="download-csv-template"
              >
                <FileText className="w-4 h-4 mr-2" /> Télécharger le modèle CSV
              </Button>
              <input
                ref={csvInputRef}
                type="file"
                accept=".csv"
                className="hidden"
                onChange={handleCsvImport}
                data-testid="csv-file-input"
              />
              <Button
                className="flex-1"
                onClick={() => csvInputRef.current?.click()}
                disabled={csvImporting}
                data-testid="csv-select-file-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                {csvImporting ? 'Import en cours...' : 'Sélectionner un fichier CSV'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================================
// QCM Results View
// ============================================================
const QCMResultsView = ({ qcmId, qcmTitle, onBack }) => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = getAuthToken();

  useEffect(() => {
    const fetchResults = async () => {
      try {
        const res = await axios.get(`${API_URL}/qcms/${qcmId}/resultats`, { headers: { Authorization: `Bearer ${token}` } });
        setResults(res.data);
      } catch { toast.error('Erreur chargement résultats'); }
      setLoading(false);
    };
    fetchResults();
  }, [qcmId, token]);

  return (
    <div className="max-w-4xl mx-auto" data-testid="qcm-results-view">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Retour
        </Button>
        <h2 className="text-lg font-semibold">Résultats : {qcmTitle}</h2>
      </div>

      {loading ? (
        <p className="text-center text-slate-500 py-12">Chargement...</p>
      ) : results.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-slate-500">
            <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p>Aucun passage enregistré pour ce QCM</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm" data-testid="results-table">
              <thead>
                <tr className="border-b bg-slate-50">
                  <th className="text-left py-3 px-4 font-medium text-slate-600">Candidat</th>
                  <th className="text-center py-3 px-4 font-medium text-slate-600">Score</th>
                  <th className="text-center py-3 px-4 font-medium text-slate-600">Pourcentage</th>
                  <th className="text-center py-3 px-4 font-medium text-slate-600">Résultat</th>
                  <th className="text-right py-3 px-4 font-medium text-slate-600">Date</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r) => (
                  <tr key={r.id} className="border-b last:border-0 hover:bg-slate-50">
                    <td className="py-3 px-4 font-medium">{r.candidat_nom || 'Anonyme'}</td>
                    <td className="py-3 px-4 text-center">{r.score}/{r.score_max}</td>
                    <td className="py-3 px-4 text-center">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                        r.pourcentage >= 70 ? 'bg-emerald-100 text-emerald-700' :
                        r.pourcentage >= 50 ? 'bg-amber-100 text-amber-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {r.pourcentage}%
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      {r.reussi === true && <span className="text-emerald-600 font-medium">Réussi</span>}
                      {r.reussi === false && <span className="text-red-600 font-medium">Non réussi</span>}
                      {r.reussi === null && <span className="text-slate-400">-</span>}
                    </td>
                    <td className="py-3 px-4 text-right text-slate-500">
                      {r.completed_at ? new Date(r.completed_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ============================================================
// Main QCM Page
// ============================================================
export const QCMPage = ({ embedded = false, readOnly = false }) => {
  const [qcms, setQcms] = useState([]);
  const [modules, setModules] = useState([]);
  const [sequences, setSequences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // list, builder, results
  const [editingQcm, setEditingQcm] = useState(null);
  const [resultsQcm, setResultsQcm] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatut, setFilterStatut] = useState('all');
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const importFileRef = React.useRef(null);
  const headers = useMemo(() => ({ Authorization: `Bearer ${getAuthToken()}` }), []);

  const fetchData = useCallback(async () => {
    try {
      const [qcmsRes, modulesRes, seqRes] = await Promise.all([
        axios.get(`${API_URL}/qcms`, { headers }),
        axios.get(`${API_URL}/modules`, { headers }),
        axios.get(`${API_URL}/sequences`, { headers }).catch(() => ({ data: [] })),
      ]);
      setQcms(qcmsRes.data);
      setModules(modulesRes.data);
      setSequences(seqRes.data);
    } catch { toast.error('Erreur de chargement'); }
    setLoading(false);
  }, [headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async (formData, moduleIds) => {
    let qcmId = editingQcm?.id;
    if (qcmId) {
      await axios.put(`${API_URL}/qcms/${qcmId}`, formData, { headers });
    } else {
      const res = await axios.post(`${API_URL}/qcms`, formData, { headers });
      qcmId = res.data?.id;
    }
    if (qcmId && Array.isArray(moduleIds)) {
      await axios.put(`${API_URL}/qcms/${qcmId}/modules`, { module_ids: moduleIds }, { headers });
    }
    await fetchData();
    setView('list');
    setEditingQcm(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API_URL}/qcms/${deleteTarget.id}`, { headers });
      toast.success('QCM supprimé');
      await fetchData();
    } catch { toast.error('Erreur de suppression'); }
    setDeleteTarget(null);
  };

  const handleDuplicate = async (qcm) => {
    try {
      const dup = { ...qcm, titre: `${qcm.titre} (copie)`, statut: 'brouillon' };
      delete dup.id; delete dup.created_at; delete dup.updated_at; delete dup.created_by;
      await axios.post(`${API_URL}/qcms`, dup, { headers });
      toast.success('QCM dupliqué');
      await fetchData();
    } catch { toast.error('Erreur de duplication'); }
  };

  const handleImportGoogleForms = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportLoading(true);
    try {
      const text = await file.text();
      const json = JSON.parse(text);
      const res = await axios.post(`${API_URL}/qcms/import-google-forms`, json, { headers });
      toast.success(`QCM "${res.data.titre}" importé avec ${res.data.questions?.length || 0} questions`);
      await fetchData();
      setEditingQcm(res.data);
      setView('builder');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Format JSON invalide';
      toast.error(`Erreur d'import : ${detail}`);
    }
    setImportLoading(false);
    if (importFileRef.current) importFileRef.current.value = '';
    setShowImportDialog(false);
  };

  const filteredQcms = qcms.filter(q => {
    if (filterStatut !== 'all' && q.statut !== filterStatut) return false;
    if (searchTerm && !q.titre.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  }).sort((a, b) => (a.titre || '').localeCompare(b.titre || '', 'fr', { sensitivity: 'base' }));

  const Wrapper = embedded ? React.Fragment : DashboardLayout;

  if (view === 'builder') {
    return (
      <Wrapper>
        <div className="p-4 md:p-6">
          <QCMBuilder
            qcm={editingQcm}
            modules={modules}
            sequences={sequences}
            onSave={handleSave}
            onBack={() => { setView('list'); setEditingQcm(null); }}
          />
        </div>
      </Wrapper>
    );
  }

  if (view === 'results' && resultsQcm) {
    return (
      <Wrapper>
        <div className="p-4 md:p-6">
          <QCMResultsView
            qcmId={resultsQcm.id}
            qcmTitle={resultsQcm.titre}
            onBack={() => { setView('list'); setResultsQcm(null); }}
          />
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <div className={embedded ? 'w-full' : 'p-4 md:p-6 max-w-7xl mx-auto'} data-testid="qcm-page">
        {/* Header */}
        {!embedded && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">QCM</h1>
            <p className="text-sm text-slate-500 mt-1">Créez et gérez vos questionnaires d'évaluation</p>
          </div>
          <div className="flex gap-2">
            <input
              ref={importFileRef}
              type="file"
              accept=".json"
              className="hidden"
              onChange={handleImportGoogleForms}
              data-testid="import-json-input"
            />
            <Button
              variant="outline"
              onClick={() => setShowImportDialog(true)}
              disabled={importLoading}
              data-testid="import-qcm-btn"
            >
              <Upload className="w-4 h-4 mr-2" /> Importer JSON
            </Button>
            <Button onClick={() => { setEditingQcm(null); setView('builder'); }} className="bg-indigo-600 hover:bg-indigo-700" data-testid="create-qcm-btn">
              <Plus className="w-4 h-4 mr-2" /> Nouveau QCM
            </Button>
          </div>
        </div>
        )}

        {/* Embedded compact header */}
        {embedded && (
        <div className="flex items-center justify-between mb-4">
          {!readOnly && <div className="flex gap-2">
            <input ref={importFileRef} type="file" accept=".json" className="hidden" onChange={handleImportGoogleForms} data-testid="import-json-input-embedded" />
            <Button size="sm" variant="outline" onClick={() => setShowImportDialog(true)} disabled={importLoading}>
              <Upload className="w-4 h-4 mr-1" /> Importer
            </Button>
            <Button size="sm" onClick={() => { setEditingQcm(null); setView('builder'); }} className="bg-indigo-600 hover:bg-indigo-700 gap-1" data-testid="create-qcm-btn">
              <Plus className="w-4 h-4" /> Nouveau QCM
            </Button>
          </div>}
        </div>
        )}

        {/* Filters */}
        <div className="flex gap-3 mb-6">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Rechercher un QCM..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
              data-testid="qcm-search"
            />
          </div>
          <Select value={filterStatut} onValueChange={setFilterStatut}>
            <SelectTrigger className="w-[160px]" data-testid="qcm-filter-statut">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              <SelectItem value="brouillon">Brouillon</SelectItem>
              <SelectItem value="publie">Publié</SelectItem>
              <SelectItem value="archive">Archivé</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* List */}
        {loading ? (
          <p className="text-center text-slate-500 py-12">Chargement...</p>
        ) : filteredQcms.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <FileText className="w-16 h-16 mx-auto mb-4 text-slate-200" />
              <h3 className="text-lg font-medium text-slate-600 mb-2">
                {qcms.length === 0 ? 'Aucun QCM créé' : 'Aucun résultat'}
              </h3>
              <p className="text-sm text-slate-400 mb-6">
                {qcms.length === 0 ? 'Créez votre premier questionnaire d\'évaluation' : 'Essayez d\'autres filtres'}
              </p>
              {qcms.length === 0 && (
                <Button onClick={() => { setEditingQcm(null); setView('builder'); }} className="bg-indigo-600 hover:bg-indigo-700">
                  <Plus className="w-4 h-4 mr-2" /> Créer un QCM
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto" data-testid="qcm-table">
            <p className="px-3 py-1.5 text-[11px] text-slate-500 border-b bg-slate-50">{filteredQcms.length} QCM — tri alphabétique</p>
            <table className="w-full text-xs min-w-[900px]">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wide text-slate-400 border-b">
                  <th className="px-3 py-2">Titre</th><th className="px-3 py-2">Modules</th><th className="px-3 py-2">Séquence</th><th className="px-3 py-2 w-20 text-center">Questions</th><th className="px-3 py-2 w-16 text-center">Points</th><th className="px-3 py-2 w-24 text-center">Savoir-faire</th><th className="px-3 py-2 w-24">Type</th><th className="px-3 py-2 w-24">Créé le</th><th className="px-3 py-2 w-20">Statut</th><th className="px-3 py-2 w-44" />
                </tr>
              </thead>
              <tbody>
                {filteredQcms.map((qcm) => (
                  <tr key={qcm.id} className="border-b last:border-b-0 hover:bg-indigo-50/40" data-testid={`qcm-card-${qcm.id}`}>
                    <td className="px-3 py-1.5">
                      <p className="font-medium text-slate-900">{qcm.titre}</p>
                      {qcm.description && <p className="text-[11px] text-slate-400 line-clamp-1">{qcm.description}</p>}
                    </td>
                    <td className="px-3 py-1.5"><QcmModulesBadges modules={qcm.modules_rattaches} qcmId={qcm.id} /></td>
                    <td className="px-3 py-1.5 text-slate-500">{qcm.sequence_code ? `${qcm.sequence_code}${qcm.sequence_name ? ` - ${qcm.sequence_name}` : ''}` : <span className="text-slate-300">—</span>}</td>
                    <td className="px-3 py-1.5 text-center text-slate-600">{qcm.questions?.length || 0}</td>
                    <td className="px-3 py-1.5 text-center text-slate-600">{qcm.questions?.reduce((s, q) => s + (q.points || 1), 0) || 0}</td>
                    <td className="px-3 py-1.5 text-center text-slate-600">{qcm.competences_visees?.length || 0}</td>
                    <td className="px-3 py-1.5 text-slate-500">{qcm.type_evaluation ? <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold ${qcmTypeColor(qcm.type_evaluation)}`} data-testid={`qcm-type-${qcm.id}`}>{qcmTypeLabel(qcm.type_evaluation)}</span> : '—'}{qcm.settings?.temps_limite_minutes ? <span className="block text-[10px] text-slate-400"><Clock className="w-3 h-3 inline mr-0.5" />{qcm.settings.temps_limite_minutes} min</span> : null}</td>
                    <td className="px-3 py-1.5 text-slate-500" data-testid={`qcm-created-at-${qcm.id}`}>{qcm.created_at ? new Date(qcm.created_at).toLocaleDateString('fr-FR') : '—'}</td>
                    <td className="px-3 py-1.5"><span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${qcm.statut === 'publie' ? 'bg-emerald-100 text-emerald-700' : qcm.statut === 'archive' ? 'bg-slate-100 text-slate-500' : 'bg-amber-100 text-amber-700'}`}>{qcm.statut === 'publie' ? 'Publié' : qcm.statut === 'archive' ? 'Archivé' : 'Brouillon'}</span></td>
                    <td className="px-3 py-1.5">
                      <div className="flex items-center justify-end gap-0.5">
                        <QcmPdfButtons qcm={qcm} className="h-7 text-xs" />
                        {!readOnly && <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { setEditingQcm(qcm); setView('builder'); }} title="Modifier" data-testid={`edit-qcm-${qcm.id}`}><Pencil className="w-3.5 h-3.5" /></Button>}
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { setResultsQcm(qcm); setView('results'); }} title="Résultats" data-testid={`results-qcm-${qcm.id}`}><BarChart3 className="w-3.5 h-3.5" /></Button>
                        {!readOnly && <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleDuplicate(qcm)} title="Dupliquer"><Copy className="w-3.5 h-3.5" /></Button>}
                        {!readOnly && <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600" onClick={() => setDeleteTarget(qcm)} title="Supprimer"><Trash2 className="w-3.5 h-3.5" /></Button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Delete Dialog */}
        <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer ce QCM ?</AlertDialogTitle>
              <AlertDialogDescription>
                Le QCM "{deleteTarget?.titre}" et tous ses résultats seront définitivement supprimés.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Import Dialog */}
        <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
          <DialogContent className="max-w-lg" data-testid="import-qcm-dialog">
            <DialogHeader>
              <DialogTitle>Importer un QCM (Google Forms JSON)</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-sm text-slate-600">
                Exportez votre Google Form en JSON via Google Apps Script, puis importez le fichier ici.
                Les types supportés sont : <b>Choix unique</b>, <b>Choix multiple</b> et <b>Vrai/Faux</b>.
              </p>
              <div className="bg-slate-50 rounded-lg p-4 text-xs text-slate-500 font-mono leading-relaxed">
                {`{
  "title": "Mon QCM",
  "items": [
    {
      "type": "MULTIPLE_CHOICE",
      "title": "Question ?",
      "points": 1,
      "choices": [
        { "value": "Réponse A", "isCorrect": true },
        { "value": "Réponse B", "isCorrect": false }
      ]
    }
  ]
}`}
              </div>
              <Button
                className="w-full"
                onClick={() => importFileRef.current?.click()}
                disabled={importLoading}
                data-testid="import-select-file-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                {importLoading ? 'Import en cours...' : 'Sélectionner un fichier JSON'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Wrapper>
  );
};

export default QCMPage;
